import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import keyDown from '@mailobj-browser/front/js/events/types/keyDown.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import append from '@mailobj-browser/front/js/tree/append.js'
import element from '@mailobj-browser/front/js/tree/element.js'
import remove from '@mailobj-browser/front/js/tree/remove.js'
import removed from '@mailobj-browser/front/js/tree/removed.js'
import array from '@mailobj-browser/front/js/utils/array.js'
import never from '@mailobj-browser/front/js/utils/never.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import override from '#utilities/js/ea/override.js'

const enabled = new WeakMap()

const handlers = new WeakMap()

const editable = 'input:not([type="checkbox"]),select,textarea,[contenteditable="true"]'

const displayElements = (modal) => {
  const { ownerDocument, dataset } = modal
  const { text } = dataset
  const translation = object(null, JSON.parse(text))

  const sections = all('section[data-kind]', modal)
  const map = new Map()
  for (const section of sections) {
    const { dataset } = section
    const { kind } = dataset
    const dl = one('dl', section)
    map.set(kind, dl)
  }

  const hotkeys = all('slot[data-name^="hotkey-"][data-title]', ownerDocument)
  for (const { dataset } of hotkeys) {
    const nodes = []
    const { content, title, requires = null, kind } = dataset

    const requirement = one(requires, ownerDocument)
    if (requires !== null && requirement === null) {
      continue
    }

    const dl = map.get(kind)
    const dt = element(dl, 'dt', { class: 'ea_auth__hotkeys_desc' }, title)
    const dd = element(dl, 'dd', { class: 'ea_auth__hotkeys_keyboard' })

    const combinaisons = content.split('+')
    for (const [key, value] of combinaisons.entries()) {
      combinaisons[key] = translation[value] ?? value
    }

    let spans = []
    for (let key of combinaisons) {
      const letter = /[A-Za-z]$/
      if (letter.test(key) && key.length === 1) {
        if (key === key.toUpperCase()) {
          const uppercase = translation['Shift']
          const shift = element(dd, 'span', { class: 'ea_auth__hotkeys_key' }, uppercase)
          spans.push(shift)
        }
        key = key.toUpperCase()
      }
      const keySpan = element(dd, 'span', { class: 'ea_auth__hotkeys_key' }, key)
      spans.push(keySpan)
    }

    const children = []
    for (const span of spans) {
      if (children.length) {
        children.push(' + ')
      }
      children.push(span)
    }

    append(dd, ...children)
    nodes.push(dt, dd)
    append(dl, nodes)
  }

  for (const [kind, dl] of map) {
    if (dl.childElementCount === 0) {
      const section = one(`section[data-kind^=${kind}`, modal)
      remove(section)
    }
  }
}

const onKeyDown = object(listener, {
  type: keyDown,
  task: async (
    document,
    event
  ) => {
    const { target } = event

    if (!enabled.has(document) || target.matches(editable)) {
      return
    }

    await run(document, target, event)
  }
})

export const run = async (document, target, event) => {
  const key = format(event)
  const element = one(`slot[data-name^="hotkey-"][data-content="${key}"]`, document)
  const handler = handlers.get(element)

  await handler?.task(element, event)
}

const enable = document => {
  enabled.set(document, true)
}

export const format = ({
  altKey,
  ctrlKey,
  metaKey,
  key
}) => {
  return [
    ...ctrlKey ? ['Control'] : [],
    ...altKey ? ['Alt'] : [],
    ...metaKey ? ['Control'] : [],
    key
  ].join('+')
}

export default array([
  displayElements
])

export const hotkey = (
  element,
  handler
) => {
  const { ownerDocument } = element

  enable(ownerDocument)
  handlers.set(element, handler)
  onKeyDown.listen(ownerDocument)
}

export const disable = (
  node,
  abort = never
) => {
  const { ownerDocument } = node

  enabled.delete(ownerDocument)

  queueMicrotask(async () => {
    await removed(node, abort)
    enable(ownerDocument)
  })
}

override(disable)
